import React, { useEffect, useState, useReducer } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import routes from 'routing/routes';

import MainFont from 'ui/typography/mainFont';
import {
  BackLink,
  Header,
  HistoryIcon,
  HistoryListButton,
  IconElement,
  LeadSubTitle,
  LeadTitle,
  LoaderWrapper,
  StatusLead
} from './style';

import { partnerRoles, StatusLead as StatusLeadConst } from 'const';
import { GET_LEAD_DETAIL } from 'redux/sagas/actions/leadDetail';
import { useHistory, useParams } from 'react-router-dom';
import { LoaderComponent, LoaderIcon } from '../../ui/components/Loader/style';
import { HistoryList } from 'ui/components';
import { MODAL_OPEN } from 'redux/slices/modals';
import ConfirmModal from 'ui/components/Modals/ConfirmModal';
import DetailSection from './DetailSection';
import DetailSectionProductPreference from './DetailsSectionProductPreference';

const CASES_UPDATES = {
  COMPANY_UPDATE: 'COMPANY_UPDATE',
  NOTE_UPDATE: 'NOTE_UPDATE',
  PRODUCTS_UPDATE: 'PRODUCTS_UPDATE'
};

const reducer = (state, action) => {
  switch (action.type) {
    case CASES_UPDATES.COMPANY_UPDATE:
      return {
        ...state,
        companyInfo: action.payload
      };
    case CASES_UPDATES.NOTE_UPDATE:
      return {
        ...state,
        note: action.payload
      };
    case CASES_UPDATES.PRODUCTS_UPDATE:
      return {
        ...state,
        productPreference: action.payload
      };
    default:
      return { ...state };
  }
};

const initialState = {
  companyInfo: false,
  productPreference: false,
  note: false
};

const LeadDetail = ({
  getLeadDetail,
  leadDetail,
  pushUrl,
  isLoading,
  role,
  openConfirmModal
}) => {
  const { t } = useTranslation();
  const { leadId } = useParams();
  const [isListClosed, setIsListClosed] = useState(true);
  const history = useHistory();

  const [blockingConditionState, dispatchReducer] = useReducer(
    reducer,
    initialState
  );

  useEffect(() => {
    getLeadDetail(leadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLeadDetail, leadId]);

  const pushToLeads = () => {
    pushUrl(routes.lead.path);
  };

  const roleForHistory = [
    partnerRoles.ADMIN,
    partnerRoles.COMMERCIAL_PARTNER_USER,
    partnerRoles.COMMERCIAL_PARTNER_SUPER_USER
  ];

  const unblock = history.block((location, action) => {
    const { companyInfo, productPreference, note } = blockingConditionState;
    if (companyInfo || productPreference || note) {
      openConfirmModal(routes.lead.path);
      return false;
    }
    return true;
  });

  useEffect(() => {
    return () => {
      unblock();
    };
  }, [blockingConditionState, openConfirmModal, history, unblock]);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <LoaderComponent>
          <LoaderIcon />
        </LoaderComponent>
      </LoaderWrapper>
    );
  }

  return (
    <>
      <Header>
        <BackLink onClick={pushToLeads}>
          <IconElement className="icon-back" />
          <MainFont
            configuration={{
              fontSize: 14,
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {t('back_leads').toUpperCase()}
          </MainFont>
        </BackLink>
        <LeadTitle>
          <MainFont configuration={{ fontSize: 24, fontWeight: 'bold' }}>
            {t('lead')} {leadId}
          </MainFont>
          <StatusLead value={leadDetail?.status?.toLowerCase()}>
            <MainFont configuration={{ fontSize: 16, fontWeight: 'bold' }}>
              {t(
                StatusLeadConst[leadDetail?.status?.toUpperCase()]
              )?.toUpperCase()}
            </MainFont>
          </StatusLead>
        </LeadTitle>
        <LeadSubTitle isListClosed={isListClosed}>
          <MainFont configuration={{ fontSize: 16 }}>
            {t('type')}: {t(leadDetail?.leadType?.toLowerCase())}
          </MainFont>
          {isListClosed && roleForHistory.includes(role) ? (
            <HistoryListButton onClick={() => setIsListClosed(false)}>
              <HistoryIcon className="icon-history" />
              <MainFont type="bold" configuration={{ fontSize: 14 }}>
                {t('history_list')}
              </MainFont>
            </HistoryListButton>
          ) : isListClosed && !roleForHistory.includes(role) ? null : (
            <HistoryList
              closeAction={setIsListClosed}
              leadHistory={leadDetail?.leadHistory}
            />
          )}
        </LeadSubTitle>
      </Header>
      {leadDetail?.data?.map((item, index) => {
        if (item.title === 'product_preferences') {
          return (
            <DetailSectionProductPreference
              leadDetailData={item}
              key={index}
              creatorEmail={leadDetail?.creatorEmail}
              leadStatus={leadDetail?.status}
              leadType={leadDetail?.leadType}
              isSaveEnabled={value => {
                dispatchReducer({
                  type: CASES_UPDATES.PRODUCTS_UPDATE,
                  payload: value
                });
              }}
            />
          );
        }
        return (
          <DetailSection
            leadDetailData={item}
            key={index}
            creatorEmail={leadDetail?.creatorEmail}
            leadStatus={leadDetail?.status}
            leadType={leadDetail?.leadType}
            isReadonlySection={item.title === 'lead_created_by'}
            isSaveEnabled={value => {
              if (item.title !== 'lead_created_by') {
                if (item.title === 'company_and_vehicle_info') {
                  dispatchReducer({
                    type: CASES_UPDATES.COMPANY_UPDATE,
                    payload: value
                  });
                } else {
                  dispatchReducer({
                    type: CASES_UPDATES.NOTE_UPDATE,
                    payload: value
                  });
                }
              }
            }}
          />
        );
      })}
      <ConfirmModal />
    </>
  );
};

LeadDetail.propTypes = {
  leadId: PropTypes.string,
  getLeadDetail: PropTypes.func,
  pushUrl: PropTypes.func,
  isLoading: PropTypes.bool,
  userEmail: PropTypes.string,
  role: PropTypes.string,
  openConfirmModal: PropTypes.func
};

export default connect(
  state => ({
    leadDetail: state?.leadDetail?.data || {},
    isLoading: state?.leadDetail?.isLoading,
    userEmail: state?.authentication?.accountInfo?.email,
    role: state?.authentication?.accountInfo?.role
  }),
  dispatch => ({
    getLeadDetail: leadId =>
      dispatch({
        type: GET_LEAD_DETAIL._REQUEST,
        payload: { leadId }
      }),
    pushUrl: url => dispatch(push(url)),
    openConfirmModal: payload =>
      dispatch(MODAL_OPEN({ id: 'ConfirmModal', payload }))
  })
)(LeadDetail);
